import React, { FC, createRef, useCallback, useEffect, useMemo, useState } from 'react';
import fp from 'lodash/fp';

import { Input, Stack } from 'Modules/ui3';

import { useFeedbackBusy } from '../../state/feedback';
import { CodeFormProps } from './types';


export const CodeForm: FC<CodeFormProps> = ({ digitsCount = 4, onCodeEnter, isError }) => {
    const [digits, setDigits] = useState<string[]>([]);
    const busy = useFeedbackBusy();

    const inputRefs = useMemo(() =>
        Array.from({ length: digitsCount }, () => createRef<HTMLInputElement>()),
    [digitsCount]);

    useEffect(() => {
        if (isError || digits.length < digitsCount)
            setDigits(Array.from({ length: digitsCount }, () => ''));
    }, [isError, digitsCount, digits.length]);


    const keyDown = useCallback((idx: number) => (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Backspace' && inputRefs[idx].current.selectionStart === 0 && idx > 0) {
            inputRefs[idx].current.blur();
            inputRefs[idx - 1].current.focus();
        }
    }, [inputRefs]);

    const change = useCallback((idx: number) => e => {
        const target = e.target as HTMLInputElement;
        const cursorPosition = target.selectionStart;

        const values = fp.pipe(
            // Если курсор установлен слева от символа, то обрезаем его, чтобы он не сместился в следующую ячейку
            value => value.length > cursorPosition ? value.slice(0, -1) : value,
            // Блокируем ввод и удаляем не цыфры при вставке из буфера
            fp.replace(/[^\d]+/g, ''),
            // Обрезаем под количество чисел кода
            value => value.substring(0, digitsCount - idx),
            fp.split(''),
        )(target.value);

        setDigits(prev => {
            const newDigits = prev.map(
                (current, index) => {
                    if (index === idx || index > idx && values[index - idx])
                        return values[index - idx];
                    return current;
                },
            );

            const code = newDigits.join('');
            if (code.length === digitsCount)
                onCodeEnter(code);

            return newDigits;
        });

        if (/\d/.test(values[0])) {
            inputRefs[idx].current.blur();
            if (idx < digitsCount - values.length)
                inputRefs[idx + values.length].current.focus();
        }
    }, [digitsCount, inputRefs, onCodeEnter]);

    return (
        <Stack direction='row' spacing={2}>
            {inputRefs.map((ref, idx) => (
                <Input
                    ref={ref}
                    key={idx}
                    name={`digit_${idx}`}
                    autoFocus={idx === 0}
                    value={digits[idx] || ''}
                    onKeyDown={keyDown(idx)}
                    onChange={change(idx)}
                    disabled={busy}
                    needEvent
                    sx={{ p: 0 }}
                    InputProps={{
                        sx: ({ typography }) => ({
                            p: 0,
                            overflow: 'hidden',

                            height: 'auto',
                            '& > input': {
                                py: 4,
                                textAlign: 'center',
                                height: 'auto',
                                ...typography.mediumHeader1,
                            },
                        }),
                    }}
                />
            ))}
        </Stack>
    );
};
